/** Notifications → Anatomy: what one notification is actually made of.
 *
 *  Additive by design. This page reads; it changes nothing, and no existing
 *  screen was touched to make room for it — entry is the sidebar, ⌘K, or a
 *  pasted URL.
 *
 *  The whole view is URL state (`?nf=`, `?channel=`, `?target_format=`,
 *  `?open=`, `?node=`), so a link to one expanded subtree of one channel can be
 *  pasted into a ticket and opens exactly as you left it.
 *
 *  `target_format`, not `format`: `format` is DRF's reserved
 *  content-negotiation parameter and an unknown value there 404s server-side
 *  before the view runs.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as anatomyApi from '@/api/notificationAnatomy';
import type { AnatomyChannel, AnatomyFormat } from '@/api/notificationAnatomy';
import * as notificationsApi from '@/api/notifications';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { notificationsHref } from '@/utils/gridLinks';

import { SYSTEM_TENANT } from '../notifications/CreateNotificationDialog';
import { notificationActions } from '../notifications/notificationLinks';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';

import { AnatomyTree, visibleRowCount } from './AnatomyTree';
import {
  allExpandableIds,
  buildNotificationTree,
  defaultExpanded,
  pathTo,
} from './model/buildAnatomy';
import type { AnatomyViewNode } from './model/types';
import { NodeDetailDrawer } from './NodeDetailDrawer';
import { StructureSummary } from './StructureSummary';
import { TracePanel } from './TracePanel';
import { ENTITY_KIND_BY_FAMILY } from './traceEntity';

const CHANNELS: AnatomyChannel[] = ['email', 'sms', 'whatsapp', 'chat', 'airbnb'];
const FORMATS: AnatomyFormat[] = ['html', 'markdown', 'plaintext'];

/** One typed query, two things it could mean.
 *
 *  Ops types either a template name ("booking confirmation") or an event code
 *  ("tour_participant.created"), so the search ORs both rather than making the
 *  user choose a mode. Both are substring matches, so a partial event code
 *  ("departure") narrows usefully. Tenant scoping is the picker's job.
 */
function searchFilter(query: string) {
  return {
    namespace: 'OR' as const,
    conditions: [
      { namespace: 'name', operator: 'icontains', value: query },
      { namespace: 'event', operator: 'icontains', value: query },
    ],
  };
}

/** A notification with no tenant is a system template — say so rather than
 *  leaving the column blank, which reads as missing data. */
/** A readable message from a console 400.
 *
 *  `ConsoleApiError.message` falls back to axios's "Request failed with status
 *  code 400" whenever the body is FIELD-KEYED rather than carrying `detail` —
 *  which is exactly the shape DRF's `ValidationError({"context": ...})` produces,
 *  i.e. every validation error this endpoint raises. Showing the axios string
 *  would throw away the only useful part.
 */
function apiMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (!(error instanceof ConsoleApiError)) return fallback;
  const body = error.body as Record<string, unknown> | undefined;
  if (body?.detail) return String(body.detail);
  for (const value of Object.values(body ?? {})) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  }
  return error.message || fallback;
}

function tenantLabel(nf: notificationsApi.NotificationListItem): string {
  return nf.tenant ? nf.tenant.code || nf.tenant.name : 'system';
}

function findNode(root: AnatomyViewNode, id: string): AnatomyViewNode | null {
  if (root.id === id) return root;
  for (const child of root.children) {
    const hit = findNode(child, id);
    if (hit) return hit;
  }
  return null;
}

export function NotificationAnatomyPage() {
  const [params, setParams] = useSearchParams();
  const snackbar = useSnackbar();

  const nfParam = params.get('nf');
  const nfId = nfParam && /^\d+$/.test(nfParam) ? Number(nfParam) : null;
  const channel = (params.get('channel') as AnatomyChannel | null) ?? undefined;
  const targetFormat = (params.get('target_format') as AnatomyFormat | null) ?? undefined;
  const selectedId = params.get('node');
  const showBlank = params.get('blank') === '1';
  // Which text the layout does NOT own is a question you ask repeatedly while
  // refactoring, so it is a sticky view setting rather than a hover.
  const highlightBlocks = params.get('hlblocks') !== '0';

  // The tenant narrows the notification search. It is URL state like everything
  // else, so a shared link lands on the same shortlist — with the console-wide
  // scope underneath it.
  const pageTenant = usePageTenant();
  const tenantParam = pageTenant.raw;
  const tenantId: number | 'system' | null =
    tenantParam === 'system' ? 'system' : tenantParam && /^\d+$/.test(tenantParam) ? Number(tenantParam) : null;

  /** Ids the URL says are open. Empty means "use the computed defaults". */
  const openParam = params.get('open');
  const urlOpen = useMemo(
    () => (openParam ? new Set(openParam.split(',').filter(Boolean)) : null),
    [openParam],
  );

  // Only the picker's display label is local state; the identity lives in the
  // URL so the view is linkable and survives a reload.
  const [picked, setPicked] = useState<notificationsApi.NotificationListItem | null>(null);
  const [pickedTenant, setPickedTenant] = useState<TenantPickerValue | null>(null);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const anatomy = useQuery({
    queryKey: ['notification-anatomy', nfId, channel ?? null, targetFormat ?? null],
    queryFn: () =>
      anatomyApi.getNotificationAnatomy(nfId!, { channel, targetFormat }),
    enabled: nfId !== null,
    // Structure changes only when someone edits the notification; a stale tree
    // is confusing rather than merely old, so keep it short.
    staleTime: 30_000,
  });

  /** A trace REPLACES the displayed payload rather than merging into it: node ids
   *  are only comparable within one (channel, format) render, so merging two
   *  responses could attach a verdict to the wrong node. */
  const [traced, setTraced] = useState<anatomyApi.NotificationAnatomy | null>(null);

  const trace = useMutation({
    mutationFn: (context: Parameters<typeof anatomyApi.traceNotificationAnatomy>[1]['context']) =>
      anatomyApi.traceNotificationAnatomy(nfId!, {
        context,
        ...(channel ? { channel } : {}),
        ...(targetFormat ? { target_format: targetFormat } : {}),
      }),
    onSuccess: setTraced,
  });

  // Any change to WHAT is being shown invalidates a trace of the old thing.
  useEffect(() => {
    setTraced(null);
    trace.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfId, channel, targetFormat]);

  const shown = traced ?? anatomy.data ?? null;

  const root = useMemo(() => (shown ? buildNotificationTree(shown) : null), [shown]);

  const expanded = useMemo(() => {
    if (!root) return new Set<string>();
    if (urlOpen) return urlOpen;
    const base = defaultExpanded(root);
    // A deep link to one node opens just that spine on top of the defaults.
    if (selectedId) pathTo(root, selectedId).forEach((id) => base.add(id));
    return base;
  }, [root, urlOpen, selectedId]);

  const writeExpanded = useCallback(
    (next: ReadonlySet<string>) => setParam('open', [...next].join(',')),
    [setParam],
  );

  const onToggle = useCallback(
    (id: string) => {
      const next = new Set(expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeExpanded(next);
    },
    [expanded, writeExpanded],
  );

  const selectedNode = useMemo(
    () => (root && selectedId ? findNode(root, selectedId) : null),
    [root, selectedId],
  );

  /** `block.pickup_details` -> the id of that block's row, so a reference in the
   *  layout can jump to the definition that fills it. */
  const blockNodeIdByAlias = useMemo(() => {
    const map = new Map<string, string>();
    if (!root) return map;
    const group = root.children.find((c) => c.kind === 'blocks');
    group?.children.forEach((b) => {
      if (b.block) map.set(b.block.alias, b.id);
    });
    return map;
  }, [root]);

  const onJumpToBlock = useCallback(
    (alias: string) => {
      const targetId = blockNodeIdByAlias.get(alias);
      if (!targetId || !root) {
        // The reference names a block this notification does not attach — the
        // stats already flag it as unknown; do not silently do nothing.
        snackbar.show(`block.${alias} is not attached to this notification.`, 'warning');
        return;
      }
      const next = new URLSearchParams(params);
      next.set('node', targetId);
      const open = new Set(expanded);
      pathTo(root, targetId).forEach((id) => open.add(id));
      open.add(targetId);
      next.set('open', [...open].join(','));
      setParams(next, { replace: true });
    },
    [blockNodeIdByAlias, root, expanded, params, setParams, snackbar],
  );

  const searchNotifications = useCallback(
    async (query: string, signal: AbortSignal) => {
      const page = await notificationsApi.list({
        page_size: 25,
        ...(tenantId !== null ? { tour_operator_id: tenantId } : {}),
        ...(query.trim() ? { filters: [searchFilter(query.trim())] } : {}),
      });
      if (signal.aborted) return [];
      return page.results;
    },
    [tenantId],
  );

  const onTenantChange = useCallback(
    (next: TenantPickerValue | null) => {
      setPickedTenant(next);
      const p = new URLSearchParams(params);
      pageTenant.writeTo(p, next ? (next.id === SYSTEM_TENANT.id ? 'system' : String(next.id)) : null);
      // The current notification may not belong to the new tenant; clearing it
      // is honest, and leaving a stale tree on screen would not be.
      ['nf', 'open', 'node', 'channel', 'target_format'].forEach((k) => p.delete(k));
      setParams(p, { replace: true });
    },
    [params, setParams, pageTenant],
  );

  const tenantValue = useMemo<TenantPickerValue | null>(() => {
    if (tenantId === null) return null;
    if (tenantId === 'system') return SYSTEM_TENANT;
    if (pickedTenant && pickedTenant.id === tenantId) return pickedTenant;
    const loaded = shown?.notification.tour_operator;
    if (loaded && loaded.id === tenantId) {
      return { id: loaded.id, name: loaded.name, code: loaded.code };
    }
    return { id: tenantId, name: `#${tenantId}` };
  }, [tenantId, pickedTenant, shown]);

  const onPick = useCallback(
    (value: notificationsApi.NotificationListItem | null) => {
      setPicked(value);
      const next = new URLSearchParams(params);
      if (value) next.set('nf', String(value.id));
      else next.delete('nf');
      // A different notification invalidates every view setting keyed to the
      // old tree; carrying them over would open arbitrary nodes.
      next.delete('open');
      next.delete('node');
      next.delete('channel');
      next.delete('target_format');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const pickerValue = useMemo(() => {
    if (nfId === null) return null;
    if (picked && picked.id === nfId) return picked;
    const loaded = shown?.notification;
    if (loaded && loaded.id === nfId) {
      return { id: loaded.id, name: loaded.name } as notificationsApi.NotificationListItem;
    }
    return { id: nfId, name: `#${nfId}` } as notificationsApi.NotificationListItem;
  }, [nfId, picked, shown]);

  const errorMessage = apiMessage(anatomy.error, 'The anatomy could not be loaded.');

  // Cheap: flattenVisible only walks expanded subtrees. Shared with the tree so
  // the caption and the tree cannot disagree about what "visible" means.
  const visibleRows = useMemo(
    () => (root ? visibleRowCount(root, expanded, showBlank) : 0),
    [root, expanded, showBlank],
  );

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        title="Anatomy"
        subtitle="What one notification is made of — layout, blocks, and the structure of every template."
        helpStory="notification-anatomy"
      />

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ minWidth: 220 }}>
            <TenantPicker
              value={tenantValue}
              onChange={onTenantChange}
              label="Tenant"
              placeholder="All tenants"
              size="small"
              extraOptions={[SYSTEM_TENANT]}
            />
            {shown?.notification.tour_operator && (
              <PickerLinks
                items={[
                  {
                    key: shown.notification.tour_operator.id,
                    label:
                      shown.notification.tour_operator.code ||
                      shown.notification.tour_operator.name,
                    actions: operatorActions(shown.notification.tour_operator, {
                      href: notificationsHref([], shown.notification.tour_operator.id),
                      label: 'Notifications of this operator',
                    }),
                  },
                ]}
              />
            )}
          </Box>

          <Box sx={{ minWidth: 340, flexGrow: 1 }}>
            <RemoteAutocomplete<notificationsApi.NotificationListItem>
              queryFn={searchNotifications}
              // Names repeat across tenants ("Booking confirmation" exists ~70
              // times), so the tenant is part of the identity, not decoration.
              getOptionLabel={(o) => `${tenantLabel(o)} · ${o.name} (#${o.id})`}
              getOptionKey={(o) => o.id}
              value={pickerValue}
              onChange={(v) => onPick((v as notificationsApi.NotificationListItem | null) ?? null)}
              label="Notification"
              placeholder="Name or event code…"
              size="small"
              fetchOnEmpty
              renderOption={(optionProps, option) => (
                <li {...optionProps} key={option.id}>
                  {/* A disabled notification is dimmed AND labelled — colour
                   *  alone would not survive for a colour-blind reader. */}
                  <Box sx={{ minWidth: 0, opacity: option.enabled ? 1 : 0.55 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: option.enabled ? 'text.primary' : 'text.secondary',
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={tenantLabel(option)}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.6875rem' }}
                      />
                      {!option.enabled && (
                        <Chip
                          size="small"
                          label="disabled"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6875rem' }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {option.event} · #{option.id}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            {/* The viewer is read-only; the editor is one click away, and the
                page had no link to it at all. */}
            {nfId !== null && (
              <PickerLinks
                items={[
                  {
                    key: nfId,
                    label: 'Open editor',
                    actions: notificationActions(nfId),
                  },
                ]}
              />
            )}
          </Box>

          <TextField
            select
            size="small"
            label="Channel"
            value={channel ?? ''}
            onChange={(e) => setParam('channel', e.target.value || null)}
            sx={{ minWidth: 140 }}
            disabled={!shown}
          >
            <MenuItem value="">
              <em>Default</em>
            </MenuItem>
            {CHANNELS.map((c) => {
              const dm = shown?.delivery_methods.find((d) => d.channel === c);
              return (
                <MenuItem key={c} value={c} disabled={!dm}>
                  {c}
                  {dm && !dm.enabled ? ' (disabled)' : ''}
                </MenuItem>
              );
            })}
          </TextField>

          <TextField
            select
            size="small"
            label="Target format"
            value={targetFormat ?? ''}
            onChange={(e) => setParam('target_format', e.target.value || null)}
            sx={{ minWidth: 150 }}
            disabled={!shown}
          >
            <MenuItem value="">
              <em>Channel default</em>
            </MenuItem>
            {FORMATS.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </TextField>

          {root && (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => writeExpanded(allExpandableIds(root))}>
                Expand all
              </Button>
              <Button size="small" onClick={() => writeExpanded(new Set([root.id]))}>
                Collapse all
              </Button>
              <Button size="small" onClick={() => setParam('blank', showBlank ? null : '1')}>
                {showBlank ? 'Hide whitespace' : 'Show whitespace'}
              </Button>
              <Button
                size="small"
                onClick={() => setParam('hlblocks', highlightBlocks ? '0' : null)}
              >
                {highlightBlocks ? 'Unshade block content' : 'Shade block content'}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {nfId === null && (
        <Typography color="text.secondary">
          Pick a notification to see its anatomy.
        </Typography>
      )}

      {anatomy.isLoading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Parsing…
          </Typography>
        </Stack>
      )}

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {shown && root && (
        <>
          <TracePanel
            entityFamily={shown.notification.event.split('.')[0] ?? ''}
            entityKind={ENTITY_KIND_BY_FAMILY[shown.notification.event.split('.')[0] ?? ''] ?? null}
            running={trace.isPending}
            error={apiMessage(trace.error, 'The trace could not be run.')}
            trace={shown.trace}
            onRun={(context) => trace.mutate(context)}
            onClear={() => {
              setTraced(null);
              trace.reset();
            }}
          />

          <Box sx={{ mb: 1.5 }}>
            <StructureSummary
              stats={shown.stats}
              truncated={shown.truncated}
              visibleRows={visibleRows}
            />
          </Box>

          {shown.notes.map((note, i) => (
            <Alert key={i} severity="info" sx={{ mb: 1, py: 0 }}>
              {note}
            </Alert>
          ))}

          {!shown.channel && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              This notification has no composition-aware delivery method (email, SMS, WhatsApp,
              chat or Airbnb), so there is no template to parse.
            </Alert>
          )}

          {/* The page root is PAGE_FILL_SX — a viewport-height flex column — so
           *  this row takes the remaining space and each column scrolls itself.
           *  That is what keeps the detail panel on screen for a 20,000-node
           *  tree; `position: sticky` could not, because the scroll container it
           *  would stick to starts far above the fold. Below `lg` there is one
           *  column, so both simply flow. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 26rem' },
              gap: 2,
              alignItems: 'start',
              flex: { lg: 1 },
              minHeight: 0,
              overflow: { lg: 'hidden' },
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                overflow: 'auto',
                minWidth: 0,
                minHeight: 0,
                height: { lg: '100%' },
                maxHeight: { xs: '60vh', lg: 'none' },
              }}
            >
              <AnatomyTree
                root={root}
                expanded={expanded}
                selectedId={selectedId}
                notificationId={shown.notification.id}
                onToggle={onToggle}
                onSelect={(id) => setParam('node', id === selectedId ? null : id)}
                onJumpToBlock={onJumpToBlock}
                showBlank={showBlank}
                highlightBlocks={highlightBlocks}
              />
            </Paper>
            <Box
              sx={{ minWidth: 0, minHeight: 0, height: { lg: '100%' }, overflowY: 'auto' }}
            >
              <NodeDetailDrawer
                node={selectedNode}
                notificationId={shown.notification.id}
                onClose={() => setParam('node', null)}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
